import { lazy, Suspense, useEffect } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { isFullwidthPath } from "@/lib/fullwidth-routes";
import SuspenseRecovery from "@/components/SuspenseRecovery";

export { queryClient } from "@/lib/queryClient";

const FullwidthManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const root = document.getElementById("root");
    if (!root) return;
    root.classList.toggle("fullwidth", isFullwidthPath(pathname));
  }, [pathname]);
  return null;
};

const Auth = lazy(() => import("@/pages/Auth"));
const Chats = lazy(() => import("@/pages/Chats"));
const ChatRoom = lazy(() => import("@/pages/ChatRoom"));
const NewChat = lazy(() => import("@/pages/NewChat"));
const CreateGroup = lazy(() => import("@/pages/CreateGroup"));
const CreateChannel = lazy(() => import("@/pages/CreateChannel"));
const Dating = lazy(() => import("@/pages/Dating"));
const Matches = lazy(() => import("@/pages/Matches"));
const Profile = lazy(() => import("@/pages/Profile"));
const Settings = lazy(() => import("@/pages/Settings"));
const ToastSettings = lazy(() => import("@/pages/ToastSettings"));
const Admin = lazy(() => import("@/pages/Admin"));
const Banking = lazy(() => import("@/pages/Premium"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const TermsAndConditions = lazy(() => import("@/pages/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TrustCenter = lazy(() => import("@/pages/TrustCenter"));
const DocsMcp = lazy(() => import("@/pages/DocsMcp"));
const ReportAbuse = lazy(() => import("@/pages/ReportAbuse"));
const StarredMessages = lazy(() => import("@/pages/StarredMessages"));
const JoinChannel = lazy(() => import("@/pages/JoinChannel"));
const Orders = lazy(() => import("@/pages/Orders"));
const BusinessProfile = lazy(() => import("@/pages/BusinessProfile"));
const ServiceBot = lazy(() => import("@/pages/ServiceBot"));
const ContactUs = lazy(() => import("@/pages/ContactUs"));
const CallHistory = lazy(() => import("@/pages/CallHistory"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const HelpGuide = lazy(() => import("@/pages/HelpGuide"));
const ProductHuntKit = lazy(() => import("@/pages/ProductHuntKit"));
const XLaunchKit = lazy(() => import("@/pages/XLaunchKit"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const Leaderboard = lazy(() => import("@/pages/Leaderboard"));
const ConnectDashboard = lazy(() => import("@/pages/ConnectDashboard"));
const ConnectStorefront = lazy(() => import("@/pages/ConnectStorefront"));
const ProgressierKit = lazy(() => import("@/pages/ProgressierKit"));
const ChannelBotQuality = lazy(() => import("@/pages/admin/ChannelBotQuality"));
const ChannelBotRuns = lazy(() => import("@/pages/admin/ChannelBotRuns"));
const SellerFulfillment = lazy(() => import("@/pages/SellerFulfillment"));
const ExploreChannels = lazy(() => import("@/pages/ExploreChannels"));
const InviteResolver = lazy(() => import("@/pages/InviteResolver"));
const Landing = lazy(() => import("@/pages/Landing"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const TableTranslator = lazy(() => import("@/pages/TableTranslator"));
const Timeline = lazy(() => import("@/pages/Timeline"));
const WhiteboardPublicView = lazy(() => import("@/pages/WhiteboardPublicView"));
const TranslatorHistory = lazy(() => import("@/pages/TranslatorHistory"));
const TranslatorHistoryDetail = lazy(() => import("@/pages/TranslatorHistoryDetail"));
const TranslatorSharedView = lazy(() => import("@/pages/TranslatorSharedView"));
const Celebrations = lazy(() => import("@/pages/Celebrations"));
const SocialCircle = lazy(() => import("@/pages/SocialCircle"));
const GhostDodgePage = lazy(() => import("@/pages/GhostDodgePage"));
const StickerCreateHarness = lazy(() => import("@/pages/__e2e/StickerCreateHarness"));
const SharedMessageResolver = lazy(() => import("@/pages/SharedMessageResolver"));
const AuthenticatedAppProviders = lazy(() => import("@/components/AuthenticatedAppProviders"));
const AppShell = lazy(() => import("@/components/AppShell.phase1"));
const AdminRoute = lazy(() => import("@/components/AdminRoute"));

// Route-level lazy chunks should swap silently — no full-screen loader on
// every navigation. Real failures still surface via the boot watchdog and
// chunk-load recovery in main.tsx / index.html.
const PageLoader = () => null;

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
    <TooltipProvider>
      <Sonner />
      <BrowserRouter>
        <FullwidthManager />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/index" element={<Landing />} />
            <Route path="/landing" element={<Landing />} />
            <Route path="/s/:token" element={<SharedMessageResolver />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/trust-center" element={<TrustCenter />} />
            <Route path="/docs/mcp" element={<DocsMcp />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/report-abuse" element={<ReportAbuse />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/__e2e/sticker-create" element={<StickerCreateHarness />} />
            <Route element={<AuthenticatedAppProviders />}>
              <Route path="/auth" element={<Auth />} />
              <Route path="/channel/join/:token" element={<JoinChannel />} />
              <Route path="/invite/:token" element={<InviteResolver />} />
              {/* Short-link aliases used by channel-bot CTAs and external shares. */}
              <Route path="/c/:token" element={<InviteResolver />} />
              <Route path="/i/:token" element={<InviteResolver />} />
              <Route element={<AppShell />}>
              <Route path="/chats" element={<Chats />} />
              <Route path="/chat/:id" element={<ChatRoom />} />
              <Route path="/chat/service-bot" element={<ServiceBot />} />
              <Route path="/new-chat" element={<NewChat />} />
              <Route path="/create-group" element={<CreateGroup />} />
              <Route path="/create-channel" element={<CreateChannel />} />
              <Route path="/explore-channels" element={<ExploreChannels />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/notifications/toasts" element={<ToastSettings />} />
              <Route path="/dating" element={<Dating />} />
              <Route path="/matches" element={<Matches />} />
              <Route path="/premium" element={<Banking />} />
              <Route path="/banking" element={<Banking />} />
              <Route element={<AdminRoute />}>
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/product-hunt" element={<ProductHuntKit />} />
                <Route path="/admin/x-launch" element={<XLaunchKit />} />
                <Route path="/admin/progressier" element={<ProgressierKit />} />
                <Route path="/admin/channel-bots" element={<ChannelBotQuality />} />
                <Route path="/admin/channel-bot-runs" element={<ChannelBotRuns />} />
              </Route>
              <Route path="/starred-messages" element={<StarredMessages />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/call-history" element={<CallHistory />} />
              <Route path="/business/:userId" element={<BusinessProfile />} />
              <Route path="/help-guide" element={<HelpGuide />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/connect-dashboard" element={<ConnectDashboard />} />
              <Route path="/fulfillment" element={<SellerFulfillment />} />
              <Route path="/translator/table" element={<TableTranslator />} />
              <Route path="/translator/history" element={<TranslatorHistory />} />
              <Route path="/translator/history/:id" element={<TranslatorHistoryDetail />} />
              <Route path="/timeline" element={<Timeline />} />
              <Route path="/timeline/:postId" element={<Timeline />} />
              <Route path="/celebrations" element={<Celebrations />} />
              <Route path="/settings/social" element={<SocialCircle />} />
              <Route path="/games/ghost-dodge" element={<GhostDodgePage />} />
              </Route>
            </Route>
            {/* Storefront is outside AppShell — public page */}
            <Route path="/storefront/:accountId" element={<ConnectStorefront />} />
            <Route path="/whiteboard/public/:token" element={<WhiteboardPublicView />} />
            <Route path="/translator/shared/:token" element={<TranslatorSharedView />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </ThemeProvider>
);

export default App;
